import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "constellation-map";

const DEFAULT_BG = "#050813";
const DEFAULT_STAR = "#fef9c3";
const DEFAULT_LINE = "#94a3b8";

const CLUSTER_DENSITY = 0.0000022; // clusters per pixel² (sparse)
const MIN_CLUSTERS = 5;
const MAX_CLUSTERS = 18;
const STARS_PER_CLUSTER_MIN = 4;
const STARS_PER_CLUSTER_MAX = 8;
const CLUSTER_SPREAD = 90;          // px radius around the cluster centre
const SKY_ROTATION = 0.0005;        // rad/frame — extremely slow
const FIELD_STARS = 220;            // tiny background stars not in any cluster

function rand(min, max) { return min + Math.random() * (max - min); }

export default function ConstellationMap({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_STAR, DEFAULT_LINE]);
  const stateRef = useRef({
    clusters: [],   // { cx, cy, stars: [{x,y,r,phase,period,base,peak}], edges: [[i,j], ...] }
    fieldStars: [], // tiny dim background stars
    angle: 0,
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_STAR),
      pickColor(colors, 2, DEFAULT_LINE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    // Build a cluster: a handful of stars near a centre point + a small
    // edge list connecting them as a connected graph (so it actually looks
    // like a constellation rather than a cloud of dots).
    const buildCluster = (cx, cy) => {
      const n = STARS_PER_CLUSTER_MIN + Math.floor(Math.random() * (STARS_PER_CLUSTER_MAX - STARS_PER_CLUSTER_MIN + 1));
      const stars = new Array(n);
      for (let i = 0; i < n; i += 1) {
        const a = rand(0, Math.PI * 2);
        const r = rand(15, CLUSTER_SPREAD);
        stars[i] = {
          // Stored in cluster-local coordinates so the whole cluster can
          // rotate as one rigid body around (cx, cy).
          lx: Math.cos(a) * r,
          ly: Math.sin(a) * r,
          x: 0, y: 0, // filled in per-frame
          radius: rand(0.9, 2.4),
          phase: rand(0, Math.PI * 2),
          period: rand(0.6, 1.8),
          base: rand(0.45, 0.7),
          peak: rand(0.92, 1.0),
        };
      }
      // Edges: a path through all stars (nearest-neighbor traversal) so
      // every constellation is one connected shape.
      const remaining = stars.map((_, i) => i);
      const order = [remaining.shift()];
      while (remaining.length) {
        const last = stars[order[order.length - 1]];
        let bestIdx = 0;
        let bestD = Infinity;
        for (let k = 0; k < remaining.length; k += 1) {
          const s = stars[remaining[k]];
          const dx = s.lx - last.lx;
          const dy = s.ly - last.ly;
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            bestIdx = k;
          }
        }
        order.push(remaining.splice(bestIdx, 1)[0]);
      }
      const edges = [];
      for (let i = 0; i < order.length - 1; i += 1) {
        edges.push([order[i], order[i + 1]]);
      }
      return { cx, cy, stars, edges };
    };

    const seed = () => {
      const target = Math.round(width * height * CLUSTER_DENSITY);
      const count = Math.max(MIN_CLUSTERS, Math.min(MAX_CLUSTERS, target));
      const clusters = new Array(count);
      // Lay clusters out roughly on a jittered grid so they don't pile up.
      const cols = Math.ceil(Math.sqrt((count * width) / Math.max(1, height)));
      const rows = Math.ceil(count / cols);
      const cellW = width / cols;
      const cellH = height / rows;
      let idx = 0;
      for (let r = 0; r < rows && idx < count; r += 1) {
        for (let c = 0; c < cols && idx < count; c += 1) {
          const cx = c * cellW + cellW / 2 + rand(-cellW * 0.25, cellW * 0.25);
          const cy = r * cellH + cellH / 2 + rand(-cellH * 0.25, cellH * 0.25);
          clusters[idx] = buildCluster(cx, cy);
          idx += 1;
        }
      }
      const fieldStars = new Array(FIELD_STARS);
      for (let i = 0; i < FIELD_STARS; i += 1) {
        fieldStars[i] = {
          x: rand(0, width),
          y: rand(0, height),
          radius: rand(0.4, 1.1),
          phase: rand(0, Math.PI * 2),
          period: rand(0.4, 1.6),
          base: rand(0.15, 0.35),
          peak: rand(0.5, 0.85),
        };
      }
      stateRef.current.clusters = clusters;
      stateRef.current.fieldStars = fieldStars;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    };

    const tick = () => {
      const [bg, starColor, lineColor] = colorsRef.current;
      const state = stateRef.current;

      // Deep space background.
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      // Background field stars (no rotation — they're far enough to feel
      // fixed against the camera). They twinkle softly.
      for (let i = 0; i < state.fieldStars.length; i += 1) {
        const s = state.fieldStars[i];
        const a = s.base + (s.peak - s.base) * (0.5 + 0.5 * Math.sin(t * s.period + s.phase));
        ctx.fillStyle = rgba(starColor, a);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Whole sky rotates very slowly; each cluster also rotates around
      // its own centre at a tiny independent rate.
      state.angle += SKY_ROTATION;
      const cosA = Math.cos(state.angle);
      const sinA = Math.sin(state.angle);
      const cw = width / 2;
      const ch = height / 2;

      // Project cluster star positions for this frame.
      for (let i = 0; i < state.clusters.length; i += 1) {
        const cl = state.clusters[i];
        // Rotate cluster centre around the sky centre.
        const dx = cl.cx - cw;
        const dy = cl.cy - ch;
        const cxR = cw + dx * cosA - dy * sinA;
        const cyR = ch + dx * sinA + dy * cosA;
        for (let j = 0; j < cl.stars.length; j += 1) {
          const s = cl.stars[j];
          s.x = cxR + s.lx;
          s.y = cyR + s.ly;
        }
        cl._cxR = cxR;
        cl._cyR = cyR;
      }

      // Draw constellation lines first (under the stars).
      ctx.lineWidth = 1;
      for (let i = 0; i < state.clusters.length; i += 1) {
        const cl = state.clusters[i];
        for (let e = 0; e < cl.edges.length; e += 1) {
          const [a, b] = cl.edges[e];
          const sa = cl.stars[a];
          const sb = cl.stars[b];
          // Line alpha follows the dimmer of its two stars so blinking stars
          // visibly tug the lines along with them.
          const aa = sa.base + (sa.peak - sa.base) * (0.5 + 0.5 * Math.sin(t * sa.period + sa.phase));
          const ab = sb.base + (sb.peak - sb.base) * (0.5 + 0.5 * Math.sin(t * sb.period + sb.phase));
          const alpha = Math.min(aa, ab) * 0.35;
          ctx.strokeStyle = rgba(lineColor, alpha);
          ctx.beginPath();
          ctx.moveTo(sa.x, sa.y);
          ctx.lineTo(sb.x, sb.y);
          ctx.stroke();
        }
      }

      // Cluster stars on top, with a soft halo for the brighter ones.
      for (let i = 0; i < state.clusters.length; i += 1) {
        const cl = state.clusters[i];
        for (let j = 0; j < cl.stars.length; j += 1) {
          const s = cl.stars[j];
          const a = s.base + (s.peak - s.base) * (0.5 + 0.5 * Math.sin(t * s.period + s.phase));
          // Halo pass.
          const haloR = s.radius * 6;
          const grad = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, haloR);
          grad.addColorStop(0, rgba(starColor, 0.45 * a));
          grad.addColorStop(1, rgba(starColor, 0));
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(s.x, s.y, haloR, 0, Math.PI * 2);
          ctx.fill();
          // Core.
          ctx.fillStyle = rgba(starColor, a);
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
